import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@bruno/ui';
import type { AdminDashboardScriptsInput } from '@bruno/shared';
import { adminGetDashboardScripts, adminUpdateDashboardScripts } from '../api/admin';

const MAX_HEAD_HTML = 20000;

const EXAMPLE_HTML = `<script async src="https://app.bruno.support/v1/site/YOUR_SITE_KEY.js"></script>
<script src="https://api.analytics.cloud.thinklink.ai/api/script.js" data-site-id="YOUR_SITE_ID" defer></script>`;

export function DashboardScriptsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-dashboard-scripts'],
    queryFn: adminGetDashboardScripts,
  });

  const [enabled, setEnabled] = useState(false);
  const [headHtml, setHeadHtml] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setHeadHtml(data.headHtml);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (body: AdminDashboardScriptsInput) => adminUpdateDashboardScripts(body),
    onSuccess: () => {
      setOkMsg('Dashboard scripts saved.');
      qc.invalidateQueries({ queryKey: ['admin-dashboard-scripts'] });
    },
    onError: (err) => {
      setOkMsg(null);
      setFormError(err instanceof Error ? err.message : 'Failed to save settings');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setOkMsg(null);

    if (headHtml.length > MAX_HEAD_HTML) {
      setFormError(`HTML is too long (${headHtml.length} / ${MAX_HEAD_HTML} characters).`);
      return;
    }

    mutation.mutate({ enabled, headHtml });
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard scripts</h1>
        <p className="text-slate-500 text-sm">
          Inject custom HTML into the <strong>&lt;head&gt;</strong> of the customer
          dashboard (<code>app.bruno.support</code>) on every page, including
          login and signup. Use it to load the Bruno widget, analytics, or other
          third-party tags on user-facing pages.
        </p>
      </div>

      <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <strong>Heads up:</strong> this HTML is injected verbatim into every
        customer dashboard session and runs with full access to that page. Only
        paste markup from sources you trust. It is never injected into this admin
        app.
      </div>

      {isLoading ? (
        <div className="text-slate-500">Loading…</div>
      ) : error ? (
        <div className="text-sm text-red-600">
          Failed to load: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      ) : (
        <Card>
          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <div>
                <div className="text-sm font-medium text-slate-800">
                  Inject scripts into the customer dashboard
                </div>
                <div className="text-xs text-slate-500">
                  When off, the HTML is saved but nothing is injected — toggle on
                  to go live.
                </div>
              </div>
            </label>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-slate-700" htmlFor="head-html">
                Custom &lt;head&gt; HTML
              </label>
              <textarea
                id="head-html"
                value={headHtml}
                onChange={(e) => setHeadHtml(e.target.value)}
                placeholder={EXAMPLE_HTML}
                spellCheck={false}
                rows={12}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-500">
                {headHtml.length} / {MAX_HEAD_HTML} characters. Paste full tags,
                e.g. <code>&lt;script async src="…"&gt;&lt;/script&gt;</code>.
              </p>
            </div>

            {formError ? <div className="text-sm text-red-600">{formError}</div> : null}
            {okMsg ? <div className="text-sm text-emerald-700">{okMsg}</div> : null}

            <div className="flex items-center justify-between border-t border-slate-200 pt-4">
              <div className="text-xs text-slate-500">
                {data?.updatedAt && data.updatedAt !== new Date(0).toISOString()
                  ? `Last updated ${new Date(data.updatedAt).toLocaleString()}`
                  : 'Never saved'}
              </div>
              <Button type="submit" loading={mutation.isPending}>
                Save settings
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
